import React from 'react';
import PropTypes from 'prop-types';
import { Trans, withI18n } from 'lingui-react';
import { compose, setDisplayName } from 'recompose';

import { px2rem } from 'decisiv-ui-utils';
import { DateRangeSelector, H3, InputGroup, P } from 'base-components';
import { Column, Container, Row } from 'styled-components-reactive-grid';

import useContext from 'utils/useContext';
import RadioButton from 'components/RadioButton';
import { reportTypes, reportTitles } from 'pages/Reports/constants';

import StaticListDropdown from './StaticListDropdown';
import ReportConfigBuilderContext from './Context';
import {
  billableStatusOptions,
  billableStatusTitles,
  configTypes,
  dateRangeTypes,
  dateRangeTypesTitles,
  instructionsByType,
  reportingPeriodOptions,
  reportingPeriodTitles,
  reportingPeriodTypes,
} from './constants';

import { formatReportingPeriod } from './utils';

const ReportConfigBuilderHeader = ({
  config,
  dateRangeTypeOptions,
  i18n,
  reportType,
  updateReportConfig,
  showBillableStatus,
}) => {
  const formattedReportingPeriod = formatReportingPeriod(config);
  const {
    [configTypes.billableStatus]: billableStatus,
    [configTypes.reportingPeriod]: reportingPeriod,
  } = config;

  return (
    <Container modifiers="padScale_0" style={{ zIndex: 2 }}>
      <Row>
        <Column modifiers={['col', 'padScale_0']}>
          <H3 modifiers="fontWeightRegular">
            <Trans id={reportTitles[reportType]} />
          </H3>
          <P>
            <Trans id={instructionsByType[reportType]} />
          </P>
        </Column>
      </Row>
      <Row style={{ marginTop: px2rem(40) }}>
        <Container modifiers="padScale_0">
          {dateRangeTypeOptions.length > 1 && (
            <Row style={{ marginBottom: px2rem(10) }}>
              <Column modifiers={['col', 'padScale_0']}>
                {dateRangeTypeOptions.map(type => (
                  <RadioButton
                    id={type}
                    key={type}
                    label={<Trans id={dateRangeTypesTitles[type]} />}
                    value={type}
                    checked={config[configTypes.dateRangeType] === type}
                    onChange={e =>
                      updateReportConfig(
                        configTypes.dateRangeType,
                        e.target.value,
                      )
                    }
                    style={{ marginRight: px2rem(20) }}
                  />
                ))}
              </Column>
            </Row>
          )}
          <Row>
            <Column modifiers={['col', 'padScale_0']}>
              <InputGroup>
                <InputGroup.Row>
                  <InputGroup.Column modifiers={['flex_1']}>
                    <StaticListDropdown
                      icon="calendar"
                      name={configTypes.reportingPeriod}
                      activeItem={reportingPeriod}
                      value={`${i18n._(
                        reportingPeriodTitles[reportingPeriod],
                      )}${
                        formattedReportingPeriod
                          ? ` (${formattedReportingPeriod})`
                          : ''
                      }`}
                      label={<Trans>Reporting Period</Trans>}
                      onChange={updateReportConfig}
                      options={reportingPeriodOptions}
                    />
                  </InputGroup.Column>
                  {showBillableStatus && (
                    <InputGroup.Column modifiers={['flex_1']}>
                      <StaticListDropdown
                        name={configTypes.billableStatus}
                        activeItem={billableStatus}
                        value={i18n._(billableStatusTitles[billableStatus])}
                        label={<Trans>Billable/Not Billable</Trans>}
                        onChange={updateReportConfig}
                        options={billableStatusOptions}
                      />
                    </InputGroup.Column>
                  )}
                </InputGroup.Row>
              </InputGroup>
            </Column>
          </Row>
        </Container>
      </Row>
      {reportingPeriod === reportingPeriodTypes.custom && (
        <Row>
          <Column
            style={{
              margin: `0 ${px2rem(-8)}`,
              maxWidth: px2rem(930),
              flex: 1,
            }}
            modifiers={['col', 'padScale_0']}
          >
            <DateRangeSelector
              multiMonth
              multiMonthDirection="past"
              onSelectDate={dates =>
                updateReportConfig(configTypes.customReportingPeriod, dates)
              }
              disabledDates={{ after: new Date() }}
            />
          </Column>
        </Row>
      )}
    </Container>
  );
};

ReportConfigBuilderHeader.propTypes = {
  config: PropTypes.shape({
    [configTypes.billableStatus]: PropTypes.string.isRequired,
    [configTypes.reportingPeriod]: PropTypes.string.isRequired,
  }).isRequired,
  dateRangeTypeOptions: PropTypes.arrayOf(
    PropTypes.oneOf(Object.values(dateRangeTypes)),
  ).isRequired,
  i18n: PropTypes.shape({
    _: PropTypes.func.isRequired,
  }).isRequired,
  reportType: PropTypes.oneOf(Object.values(reportTypes)).isRequired,
  updateReportConfig: PropTypes.func.isRequired,
  showBillableStatus: PropTypes.bool,
};

ReportConfigBuilderHeader.defaultProps = {
  showBillableStatus: true,
};

export default compose(
  setDisplayName('ReportConfigBuilderHeader'),
  useContext(ReportConfigBuilderContext),
  withI18n(),
)(ReportConfigBuilderHeader);



// WEBPACK FOOTER //
// ./src/compositions/Reports/ReportConfigBuilder/Header.js